import React, { useRef, useState, useEffect } from "react";
import { Card, Button, Form, Spinner } from "reactstrap";
import { useTranslation } from "context/TranslationProvider";
import { useNotification } from "providers/NotificationProvider";
import ViewUploadedID from "modals/ViewUploadedID";

const EvidenceUploadCard = ({
  onUpload,
  loading,
}) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const inputRef = useRef(null);
  const [files, setFiles] = useState([]);

  const [idUrl, setIdUrl] = useState(null);
  const [viewUploadedIDOpen, setViewUploadedIDOpen] = useState(false);

  useEffect(() => {
    return () => {
      files.forEach((f) => {
        if (f.preview) URL.revokeObjectURL(f.preview);
      });
    };
  }, [files]);
  const closeUploadedID = () => setViewUploadedIDOpen(false);
  const zoomImage = (url) => {
    setIdUrl(url);
    setViewUploadedIDOpen(true);
  };

  const handleChange = (e) => {
    const selected = Array.from(e.target.files || []);
    if (!selected.length) return;

    const MAX_SIZE = 2 * 1024 * 1024;
    const oversized = selected.filter((f) => f.size > MAX_SIZE);
    if (oversized.length > 0) {
      oversized.forEach((f) =>
        notify(
          "error",
          t(
            "page.support-tickets.validation.fileTooLarge",
            `"${f.name}" exceeds the 2 MB limit and was not added.`
          ).replace("{filename}", f.name)
        )
      );
    }

    const validFiles = selected.filter((f) => f.size <= MAX_SIZE);
    if (!validFiles.length) {
      e.target.value = "";
      return;
    }

    if (files.length + validFiles.length > 5) {
      notify("error", t("page.support-tickets.validation.tooManyFiles", "You can only upload up to 5 files."));
    }

    const remainingSlots = 5 - files.length;
    const filesToAdd = validFiles.slice(0, remainingSlots);

    setFiles((prev) => [
      ...prev,
      ...filesToAdd.map((f) => ({
        file: f,
        preview: URL.createObjectURL(f),
        isImage: f.type.startsWith("image/"),
      })),
    ]);

    e.target.value = "";
  };

  const removeFile = (index) => {
    if (loading) return;
    setFiles((prev) => {
      const removed = prev[index];
      if (removed?.preview) {
        URL.revokeObjectURL(removed.preview);
      }
      return prev.filter((_, i) => i !== index);
    });
  };

const handleUploadClick = async () => {
  if (!files.length) return;

  await onUpload(files.map((f) => f.file));
  
  files.forEach((f) => {
    if (f.preview) URL.revokeObjectURL(f.preview);
  });

  setFiles([]);
};

  return (
    <Card className="border-0 shadow-none m-0 p-0 bg-transparent">
      <div className="pb-4">
        <Form>
          <div className="mb-3 position-relative">
            <input
              ref={inputRef}
              type="file"
              multiple
              accept="image/*,.pdf,.doc,.docx"
              className="d-none"
              id="evidenceUpload"
              onChange={handleChange}
              disabled={loading || files.length >= 5}
            />
            <label
              htmlFor="evidenceUpload"
              className={`evidence-upload-label ${loading || files.length >= 5 ? "disabled" : ""}`}
              onMouseEnter={(e) => {
                if (!loading && files.length < 5) e.currentTarget.style.borderColor = "#0056b3";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e2e8f0";
              }}
            >
              <div className="evidence-upload-title">
                {files.length
                  ? t("common.filesSelected", "{count} file(s) selected").replace("{count}", files.length)
                  : t("page.support-tickets.dragDropHint", "Drag and drop files here, or click to browse")}
              </div>
              <div className="evidence-upload-subtitle">
                {t("page.support-tickets.fileTypesHint", "PNG, JPG, or PDF — max 2 MB per file")}
              </div>
            </label>
          </div>

          {files.length > 0 && (
            <div className="d-flex align-items-center justify-content-between mt-3 mb-2">
              <span className="text-sm text-muted">
                {t("common.selectedFilesList", "Selected Files:")}
              </span>
              <Button
                color="primary"
                size="md"
                onClick={handleUploadClick}
                disabled={loading}
                className="evidence-upload-btn"
              >
                {loading ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    {t("common.uploading", "Uploading...")}
                  </>
                ) : (
                  t("common.upload", "Upload")
                )}
              </Button>
            </div>
          )}

          {files.length > 0 && (
            <div className="mt-2 d-flex flex-wrap align-items-center">
              {files.map((file, idx) => (
                <div
                  key={idx}
                  className="position-relative mr-3 mb-3 evidence-upload-img-box"
                >
                  {file.isImage ? (
                    <img
                      src={file.preview}
                      alt={file?.file?.name || ""}
                      className="rounded shadow-sm border cursor-pointer evidence-upload-img"
                      onClick={() => zoomImage(file.preview)}
                    />
                  ) : (
                    <div className="rounded border bg-light cursor-pointer d-flex align-items-center justify-content-center evidence-upload-img-box"
                      onClick={() => window.open(file.preview, "_blank", "noopener,noreferrer")}
                    >
                      <span className="text-center px-1 evidence-upload-file-name">📄 {(file?.file?.name || "").slice(0, 8)}...</span>
                    </div>
                  )}
                  {!loading && (
                    <button
                      type="button"
                      onClick={() => removeFile(idx)}
                      className="evidence-upload-remove-btn"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}

              {files.length < 5 && !loading && (
                <label
                  htmlFor="evidenceUpload"
                  className="cursor-pointer d-flex align-items-center justify-content-center rounded border mr-3 mb-3 evidence-upload-placeholder"
                >
                  <span className="evidence-upload-placeholder-icon">+</span>
                </label>
              )}
            </div>
          )}
        </Form>
      </div>
      <ViewUploadedID
        idUrl={idUrl}
        isOpen={viewUploadedIDOpen}
        toggle={closeUploadedID}
      />
    </Card>
  );
};

export default EvidenceUploadCard;
